import Lucide from "src/base-components/Lucide";
import CallerHeader from "../calllogsHeader";
import { CallerInfo } from "../CallerInfo";
import { ExistingQuery } from "../ExistingQuery";
import SlideoverModal from "src/components/SlideoverModal";
import CreateQuery from "./CreateQuery";
import { useEffect, useState } from "react";
import { OngoingBadge } from "./OngoingBadge";
import { useSelector } from "react-redux";
import {
  selectCallDetail,
  selectCustomerInfo,
  selectUpdateCall,
} from "src/stores/calllogs";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Button from "src/base-components/Button";
import { QueueInfo } from "../QueueInfo";

type QueryType = {
  id: string;
  query_no?: string;
  customer_id?: string;
  [key: string]: any;
};

const OrText = () => (
  <div>
    <div className="flex items-center my-4">
      <div className="flex-grow border-t border-gray-400"></div>
      <span className="mx-4 text-gray-500">Or</span>
      <div className="flex-grow border-t border-gray-400"></div>
    </div>
  </div>
);

const schema = yup.object().shape({
  query: yup
    .string()
    .nullable()
    .test("conditional-required", "This field is required", (value, ctx) => {
      // parent passes value prop; if mode is "others" then require
      const isOthers = ctx?.options?.context?.isOthers;
      if (!isOthers) return true; // validation OFF
      return value?.trim() !== ""; // required only for others
    })
    .test("no-leading-space", "Cannot start with a space", (value) => {
      if (!value) return true;
      return !/^\s/.test(value);
    }),
});

export const CallAnswered = ({
  callerInfo,
  //  onClose,
  updateCallDetail,
}) => {
  const [selectedType, setSelectedType] = useState<
    "existing" | "create" | "others" | null
  >(null);
  const [submitted, setSubmitted] = useState(false);
  const callDetail = useSelector(selectCallDetail);
  const customerInfo = useSelector(selectCustomerInfo);
  const result = customerInfo?.data?.result;
  // "existing" | "create" | "others"

  const [selectedQuery, setSelectedQuery] = useState<QueryType | null>(null);
  const { data } = useSelector(selectUpdateCall);
  const [othersText, setOthersText] = useState("");
  const [isQueryOpen, setIsQueryOpen] = useState(false);
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: { query: "" },
    resolver: yupResolver(schema),
    context: { selectedType },
  });

  useEffect(() => {
    const queryId = callDetail?.data?.call?.query_id;
    const remarks = callDetail?.data?.call?.remarks;

    if (queryId) {
      setSelectedType("existing");
      setSelectedQuery({ id: queryId });
      setSubmitted(true);
    } else if (remarks) {
      setSelectedType("others");
      setOthersText(remarks);
      setValue("query", remarks);
      setSubmitted(true);
      // Cache API remarks locally for PostCallRemarks prefill
      const sessionId = callerInfo?.sessionId;
      if (sessionId && typeof remarks === "string") {
        try {
          localStorage.setItem(`callRemarks_${sessionId}`, remarks);
        } catch (e) {
          // ignore storage errors
        }
      }
    }
  }, [callDetail, setValue]);

  useEffect(() => {
    if (selectedType !== "others") {
      reset({ query: "" });
    }
  }, [selectedType]);
  console.log("result?.customer", result);
  const onSubmit = async () => {
    setSubmitted(false);
    const customerId = result?.customer?.customer_id ?? "";
    const safeOthers = (othersText || "").trim();
    const sessionId = callerInfo?.sessionId;
    const storedNewlyQueryId = localStorage.getItem("newlyQuery");

    let payload: any = {
      customer_id: customerId,
      query_id: "",
      remarks: "",
      call_type: "",
      call_outcome: "",
      queue: 1,
      is_new_query: null,
    };

    if (selectedType === "existing" && selectedQuery) {
      payload.query_id = selectedQuery.id;
      payload.is_new_query =
        selectedQuery.id === storedNewlyQueryId ? true : null;
    } else if (selectedType === "create" && selectedQuery) {
      payload.query_id = selectedQuery.id;
      payload.is_new_query = true;
    } else if (selectedType === "others" && safeOthers !== "") {
      payload.remarks = safeOthers;
    }

    try {
      await updateCallDetail(sessionId, payload);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (data?.success === true) {
      setSubmitted(true);
    }
  }, [data?.success]);

  const isRemarksFilled = othersText.trim().length > 0;
  const handleCloseQueryModal = (payload?: any) => {
    if (isRemarksFilled) return; // 🚫 hard stop

    setIsQueryOpen(false);

    if (payload?.createdQuery) {
      setSelectedType("create");
      setSelectedQuery(payload.createdQuery);
      return;
    }

    setSelectedType(null);
    setSelectedQuery(null);
    setOthersText("");
  };
  const handleSelectExistingQuery = (query: any | null) => {
    if (submitted) return;
    // If toggled to unselect (null), clear selection and type
    if (!query) {
      setSelectedType(null);
      setSelectedQuery(null);
      return;
    }
    setSelectedType("existing");
    setSelectedQuery(query);
    setOthersText("");
  };
  const handleOpenCreateQuery = () => {
    setSelectedType("create");
    setSelectedQuery(null);
    setOthersText("");
    setIsQueryOpen(true);
  };

  const handleOthersChange = (value: any) => {
    if (submitted) return;
    setSelectedType("others");
    setOthersText(value ?? "");
    setSelectedQuery(null);
    // Persist user-typed remarks for downstream PostCallRemarks
    const sessionId = callerInfo?.sessionId;
    const text = (value ?? "").trim();
    if (sessionId && text.length > 0) {
      try {
        localStorage.setItem(`callRemarks_${sessionId}`, text);
      } catch (e) {
        // ignore storage errors
      }
    }
  };
  const isConfirmDisabled =
    selectedType === null ||
    submitted ||
    (selectedType === "others" && othersText.trim() === "");

  const isCreateDisabled = submitted || selectedType === "existing";
  const isTextareaDisabled =
    submitted || selectedType === "existing" || selectedType === "create";

  const headerLeft = () => (
    <p className="text-[#475569] text-xl">Create New Query</p>
  );

  const headerRight = () => (
    <div className="flex items-center space-x-3">
      <OngoingBadge
        callerInfo={callerInfo}
        className="flex items-center gap-x-1 bg-green-200 rounded-full px-4"
      />
      <button
        className="focus:outline-none hover:bg-white/100 bg-white/5 transition-all hover:rotate-180 flex items-center justify-center border rounded-full w-6 h-6"
        onClick={handleCloseQueryModal}
      >
        <Lucide className="w-9 h-9 stroke-[1]" icon="X" />
      </button>
    </div>
  );

  return (
    <>
      <CallerHeader callerInfo={callerInfo} />
      <CallerInfo callerInfo={callerInfo} />
      {callerInfo?.callActionInput && callerInfo?.callActionQueue && (
        <QueueInfo callerInfo={callerInfo} />
      )}
      {result && (
        <>
          {(() => {
            const normalizedCode = String(callerInfo?.code || "")
              .trim()
              .toLowerCase();
            const isAnswered = normalizedCode === "answered";
            return (
              <ExistingQuery
                selectedQuery={selectedQuery}
                onSelect={handleSelectExistingQuery}
                disabled={submitted || selectedType === "others"}
                hideSelect={!isAnswered}
                code={callerInfo?.code}
              />
            );
          })()}
        </>
      )}
      <div>
        {result && <OrText />}
        <button
          className={`w-full flex items-center justify-center gap-x-1 font-medium py-3.5 rounded-md border transition-all
          ${
            isCreateDisabled || isRemarksFilled
              ? "bg-gray-300 border-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-[#EFEFEF] border-[#BEBEBE] text-[#475569] hover:bg-gray-100"
          }`}
          onClick={handleOpenCreateQuery}
          disabled={isCreateDisabled || isRemarksFilled}
        >
          <Lucide color="#475569" className="w-4 h-4" icon="Plus" /> Create New
          Query
        </button>
        <OrText />
        {/* <QueryForm
          isConfirmDisabled={isConfirmDisabled}
          value={othersText}
          onChange={handleOthersChange}
        /> */}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3.5">
          <label className="font-medium text-[#0A0A0A]">Remarks</label>

          <Controller
            name="query"
            control={control}
            disabled={isTextareaDisabled}
            render={({ field }) => (
              <textarea
                {...field}
                onChange={(e) => {
                  field.onChange(e.target.value);
                  handleOthersChange(e.target.value);
                }}
                value={field.value}
                placeholder="Type your query here..."
                className={`w-full h-28 rounded-lg border px-3 py-2 text-sm outline-none ${
                  errors.query ? "border-red-500" : "border-gray-300"
                }`}
              />
            )}
          />

          <Button
            type="submit"
            className="w-full bg-[#233580] text-white py-3 rounded-lg text-sm font-medium"
            disabled={isConfirmDisabled}
          >
            Confirm
          </Button>
        </form>
      </div>

      <SlideoverModal
        visible={isQueryOpen}
        onClose={handleCloseQueryModal}
        headerLeft={headerLeft()}
        headerRight={headerRight()}
        headerItemsCenter="items-center"
        size="xl"
        className="max-h-screen overflow-y-auto"
      >
        <CreateQuery onClose={handleCloseQueryModal} callerInfo={callerInfo} />
      </SlideoverModal>
    </>
  );
};
